// Pinned to 4.0.1 (explicit, NOT the stale `latest` alias): 4.0.1 is the first version
// with `form.updateCard(...)`, which PATCHes an existing vaulted card so the saved-card
// one-click can re-collect its CVC client-side (outbound CMP Card Update). 3.2.1 could
// only POST /cards (create), which is why the old CVC re-prompt needed an inbound route.
const VGS_COLLECT_CDN = "https://js.verygoodvault.com/vgs-collect/4.0.1/vgs-collect.js";

let loadPromise: Promise<void> | null = null;

/**
 * Loads VGS Collect JS from CDN. Idempotent — subsequent calls return the same promise.
 */
export function loadVgsCollectScript(): Promise<void> {
  if (loadPromise) {
    console.log("[PaymentKit] VGS Collect CDN load already in progress — reusing promise");
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    if (typeof window !== "undefined" && window.VGSCollect) {
      console.log("[PaymentKit] VGS Collect JS already available on window");
      resolve();
      return;
    }

    console.log(`[PaymentKit] Loading VGS Collect JS from CDN: ${VGS_COLLECT_CDN}`);
    const script = document.createElement("script");
    script.src = VGS_COLLECT_CDN;
    script.async = true;
    script.onload = () => {
      console.log("[PaymentKit] VGS Collect JS loaded successfully from CDN");
      resolve();
    };
    script.onerror = () => {
      console.error(`[PaymentKit] Failed to load VGS Collect JS from CDN: ${VGS_COLLECT_CDN}`);
      loadPromise = null;
      reject(new Error("Failed to load VGS Collect JS"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Initializes a VGS Collect form instance.
 */
export function initVgsCollect(vaultId: string, environment: string): VGSCollectForm {
  if (!window.VGSCollect) {
    throw new Error("VGS Collect JS not loaded. Call loadVgsCollectScript() first.");
  }
  return window.VGSCollect.create(vaultId, environment, () => {});
}

/**
 * Fired as the shopper types the card number, with the VGS-detected network brand
 * (lowercase, e.g. "visa"/"mastercard"/"amex") or null when the field is empty or
 * the brand is unrecognized.
 */
export type CardBrandCallback = (brand: string | null) => void;

/**
 * Mounts a STANDALONE VGS Collect CVC field for the saved-card one-click
 * (CIT_SAVED_CARD_ONE_CLICK §14). Unlike {@link createVgsCardFields}, this mounts
 * only the security-code field — there is no PAN or expiry, because the card is
 * already vaulted. The field is tokenized with `form.updateCard(...)` against the
 * OUTBOUND CMP Card Update API (PATCH /cards/{cmpCardId}), NOT `form.createCard()`
 * (which requires a PAN), so a CVC-only mount is a valid, PCI-safe way to collect the
 * re-entered security code.
 *
 * Reuses the same `cardCVCField` type, iframe styling, and cleanup approach as the
 * full three-field mount above.
 *
 * @returns cleanup function that disconnects observers and removes the iframe.
 */
export function createVgsCvcOnlyField(
  form: VGSCollectForm,
  mountSelector: string,
  css?: Record<string, string>,
  onFocusChange?: (isFocused: boolean) => void,
  placeholder?: string,
): () => void {
  const sampleContainer = document.querySelector(mountSelector) as HTMLElement | null;
  const containerPadding = sampleContainer ? getComputedStyle(sampleContainer).padding : undefined;

  const baseCss = css ?? {
    "font-size": "1rem",
    color: "#18181b",
    "font-family": "ui-sans-serif, system-ui, sans-serif",
  };
  const fieldCss = {
    ...baseCss,
    height: "100%",
    "box-sizing": "border-box",
    ...(containerPadding && containerPadding !== "0px" ? { padding: containerPadding } : {}),
  };

  // Remove stale iframes from a previous mount before creating the field (VGS's
  // unmount() leaves iframes in the DOM). Must run BEFORE cardCVCField below.
  const staleContainer = document.querySelector(mountSelector);
  if (staleContainer) {
    for (const iframe of staleContainer.querySelectorAll("iframe")) {
      iframe.remove();
    }
  }

  const cvcField = form.cardCVCField(mountSelector, {
    placeholder: placeholder ?? "CVC",
    validations: ["required", "validCardSecurityCode"],
    css: fieldCss,
  });

  const container = document.querySelector(mountSelector);
  const observers: MutationObserver[] = [];
  if (container) {
    const styleIframe = (iframe: HTMLIFrameElement) => {
      const containerEl = container as HTMLElement;
      if (getComputedStyle(containerEl).position === "static") {
        containerEl.style.position = "relative";
      }
      iframe.style.position = "absolute";
      iframe.style.inset = "0";
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.style.border = "none";
    };
    const observer = new MutationObserver(() => {
      const iframe = container.querySelector("iframe") as HTMLIFrameElement | null;
      if (iframe && iframe.style.position !== "absolute") {
        styleIframe(iframe);
      }
    });
    observer.observe(container, { childList: true, subtree: true });
    observers.push(observer);
    const existing = container.querySelector("iframe") as HTMLIFrameElement | null;
    if (existing && existing.style.position !== "absolute") {
      styleIframe(existing);
    }
  }

  if (onFocusChange) {
    cvcField.on("focus", () => onFocusChange(true));
    cvcField.on("blur", () => onFocusChange(false));
  }

  return () => {
    for (const observer of observers) {
      observer.disconnect();
    }
    if (container) {
      for (const iframe of container.querySelectorAll("iframe")) {
        iframe.remove();
      }
    }
  };
}

/**
 * Creates and mounts VGS Collect card fields using CMP-specific field methods.
 *
 * Uses cardNumberField(), cardExpirationDateField(), cardCVCField() which are
 * required for the CMP createCard() flow. After mounting, call form.createCard()
 * to create a card in the VGS vault, which returns aliases for PAN and CVC.
 *
 * @param form - VGS Collect form instance
 * @param mountPoints - CSS selectors for PAN, expiry, and CVC containers
 * @param css - Optional CSS to apply to VGS Collect iframe fields
 * @param onFocusChangeCallbacks - Per-field focus/blur callbacks
 * @param placeholders - Per-field placeholder text
 * @param onCardBrandCallbacks - Per-field card-brand callback (only card_pan is honored)
 * @param onFirstInput - Fired once (per mount) with the field key on the first focus of
 *   any card field, for the first-input telemetry signal. Independent of the merchant
 *   focus callbacks above, so it fires even when the merchant passed none.
 */
export function createVgsCardFields(
  form: VGSCollectForm,
  mountPoints: { pan: string; exp: string; cvc: string },
  css?: Record<string, string>,
  onFocusChangeCallbacks?: Partial<Record<string, (isFocused: boolean) => void>>,
  placeholders?: Partial<Record<string, string>>,
  onCardBrandCallbacks?: Partial<Record<string, CardBrandCallback>>,
  onFirstInput?: (field: string) => void,
): () => void {
  // Read container padding so the VGS input content matches the merchant's
  // visual intent. The iframe uses absolute positioning (inset: 0) to fill
  // the full container, so the input needs its own padding for alignment.
  const sampleContainer = document.querySelector(mountPoints.pan) as HTMLElement | null;
  const containerPadding = sampleContainer ? getComputedStyle(sampleContainer).padding : undefined;

  const baseCss = css ?? {
    "font-size": "1rem",
    color: "#18181b",
    "font-family": "ui-sans-serif, system-ui, sans-serif",
  };

  const fieldCss = {
    ...baseCss,
    height: "100%",
    "box-sizing": "border-box",
    ...(containerPadding && containerPadding !== "0px" ? { padding: containerPadding } : {}),
  };

  // Remove stale iframes from previous VGS form mounts to prevent overlap.
  // VGS's form.unmount() does not remove iframes from the DOM.
  // IMPORTANT: This must run BEFORE VGS field methods below, because VGS may
  // insert new iframes synchronously — removing after would destroy fresh iframes.
  for (const selector of [mountPoints.pan, mountPoints.exp, mountPoints.cvc]) {
    const container = document.querySelector(selector);
    if (!container) continue;
    const staleIframes = container.querySelectorAll("iframe");
    for (const iframe of staleIframes) {
      iframe.remove();
    }
  }

  const panField = form.cardNumberField(mountPoints.pan, {
    placeholder: placeholders?.card_pan ?? "Card number",
    validations: ["required", "validCardNumber"],
    css: fieldCss,
  });

  const expField = form.field(mountPoints.exp, {
    type: "card-expiration-date",
    name: "exp-date",
    placeholder: placeholders?.card_exp ?? "MM / YY",
    yearLength: 2,
    serializers: [{ name: "separate", options: { monthName: "month", yearName: "year" } }],
    validations: ["required", "validCardExpirationDate"],
    css: fieldCss,
  });

  const cvcField = form.cardCVCField(mountPoints.cvc, {
    placeholder: placeholders?.card_cvc ?? "123",
    validations: ["required", "validCardSecurityCode"],
    css: fieldCss,
  });

  // Track observers for cleanup
  const observers: MutationObserver[] = [];
  const containers: Element[] = [];

  // VGS Collect doesn't set width/height on its iframes, so they may not
  // fill the merchant's container. Force them to stretch like direct-mode iframes.
  // Iframes may be inserted asynchronously, so use MutationObserver as a fallback.
  for (const selector of [mountPoints.pan, mountPoints.exp, mountPoints.cvc]) {
    const container = document.querySelector(selector);
    if (!container) continue;
    containers.push(container);

    const styleIframe = (iframe: HTMLIFrameElement) => {
      // Use absolute positioning so the iframe covers the full container
      // including any padding the merchant's CSS applies.
      const containerEl = container as HTMLElement;
      if (getComputedStyle(containerEl).position === "static") {
        containerEl.style.position = "relative";
      }
      iframe.style.position = "absolute";
      iframe.style.inset = "0";
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.style.border = "none";
    };

    // Always set up a MutationObserver to catch asynchronously inserted iframes.
    // VGS inserts iframes after field creation, so we need the observer even if
    // no iframe exists yet.
    const observer = new MutationObserver(() => {
      const iframe = container.querySelector("iframe") as HTMLIFrameElement | null;
      if (iframe && iframe.style.position !== "absolute") {
        styleIframe(iframe);
      }
    });
    observer.observe(container, { childList: true, subtree: true });
    observers.push(observer);

    // Style any iframe VGS may have inserted synchronously during field creation.
    // The MutationObserver only fires on future mutations, so this handles the
    // case where VGS inserts the iframe before the observer is attached.
    const existing = container.querySelector("iframe") as HTMLIFrameElement | null;
    if (existing && existing.style.position !== "absolute") {
      styleIframe(existing);
    }
  }

  // Per-field focus/blur wiring: the merchant-facing focus callback (unchanged) plus a
  // once-per-mount first-input signal. The fieldMap is iterated regardless of whether
  // the merchant passed focus callbacks, so first-input is captured even when it did not.
  let firstInputFired = false;
  const fieldMap: [VGSCollectField, string][] = [
    [panField, "card_pan"],
    [expField, "card_exp"],
    [cvcField, "card_cvc"],
  ];
  for (const [field, key] of fieldMap) {
    const cb = onFocusChangeCallbacks?.[key];
    if (onFirstInput || cb) {
      field.on("focus", () => {
        if (onFirstInput && !firstInputFired) {
          firstInputFired = true;
          onFirstInput(key);
        }
        cb?.(true);
      });
    }
    if (cb) {
      field.on("blur", () => cb(false));
    }
  }

  // Card-brand detection: VGS emits `update` on the card-number field as the shopper
  // types, and its state carries the detected `cardType` (e.g. "mastercard") before
  // the number is complete — the equivalent of NMI Collect.js `lookUpCallback`. We fire
  // the merchant callback only when the detected brand actually changes, so it isn't
  // spammed on every keystroke. `lastBrand` starts at null — the same value an empty
  // field reports — so the initial empty `update` VGS emits on mount does not fire a
  // spurious null. Brand lives on the PAN field, so only card_pan applies.
  const onCardBrand = onCardBrandCallbacks?.card_pan;
  if (onCardBrand) {
    let lastBrand: string | null = null;
    panField.on("update", (state) => {
      const brand = state.cardType ?? null;
      if (brand === lastBrand) return;
      lastBrand = brand;
      onCardBrand(brand);
    });
  }

  // Return cleanup function to disconnect observers and remove iframes
  return () => {
    for (const observer of observers) {
      observer.disconnect();
    }
    for (const container of containers) {
      const iframes = container.querySelectorAll("iframe");
      for (const iframe of iframes) {
        iframe.remove();
      }
    }
  };
}
